import React, {FC, useEffect, useMemo, useState} from "react";
import GatewayService from "../service/GatewayService";
import {ContractNote, Note, TradeType} from "../models/ContractNote";
import {DropDownCompString, getKeyVal} from "./util/DropDownComp";
import {useClients} from "../context/ClientsContext";
import {toast} from "react-hot-toast";
import {TimeUtil} from "../util/TimeUtil";
import Calendar from "react-calendar";
import 'react-calendar/dist/Calendar.css';
import './Calendar.css';
import AppConstants from "../util/AppConstants";

function computePayOut(item: Note): number {
    if (item.type !== TradeType.FUTURE) {
        return -item.quantity * item.enteredAt;
    }
    const cp = item.closingPrice ?? 0;
    return item.quantity * (cp - item.enteredAt);
}

const Money: FC<{val:number}> = ({val}) => {
    const formatted = val.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
    return <span>{formatted}</span>
}

const Section: FC<{title:string, rows:Note[]}> = ({title, rows}) => {
    const totalQty = rows.reduce((a,b)=>a + b.quantity, 0);
    const totalPayout = rows.reduce((a,b)=>a + computePayOut(b), 0);
    const totalBrokerage = rows.reduce((a,b)=>a + (b.brokerage||0), 0);
    const totalTaxes = rows.reduce((a,b)=>a + (b.taxes||0), 0);
    return <div className="control-group">
        <h3>{title} ({rows.length})</h3>
        <table className="margins-table">
            <thead>
            <tr>
                <th>Type</th>
                <th>Symbol</th>
                <th>Order Id</th>
                <th>Entry</th>
                <th>Close</th>
                <th>Qty</th>
                <th>Brokerage</th>
                <th>Taxes</th>
                <th>Pay in/out</th>
            </tr>
            </thead>
            <tbody>
            {rows.map((r, idx)=>
                <tr key={idx}>
                    <td>{r.type}</td>
                    <td>{r.symbol}</td>
                    <td>{r.orderId || '-'}</td>
                    <td>{r.enteredAt.toFixed(2)}</td>
                    <td>{r.closingPrice ?? '-'}</td>
                    <td>{r.quantity}</td>
                    <td><Money val={r.brokerage||0}/></td>
                    <td><Money val={r.taxes||0}/></td>
                    <td><Money val={computePayOut(r)}/></td>
                </tr>
            )}
            </tbody>
            <tfoot>
            <tr>
                <th colSpan={5} style={{textAlign:'right'}}>Totals</th>
                <th>{totalQty}</th>
                <th><Money val={totalBrokerage}/></th>
                <th><Money val={totalTaxes}/></th>
                <th><Money val={totalPayout}/></th>
            </tr>
            </tfoot>
        </table>
    </div>
}

export const ContractNoteComponent: FC = () => {
    const  clients  = AppConstants.CLIENTS;
    // const { clients } = useClients();
    const [clientId, setClientId] = useState<string>("");

    const todayStr = TimeUtil.toUIDate(new Date());
    const [date, setDate] = useState(todayStr);

    const [note, setNote] = useState<ContractNote|null>(null);
    const [loading, setLoading] = useState(false);
    const [isPreview, setIsPreview] = useState(true);
    const [availableDates, setAvailableDates] = useState<string[]>([]);
    const [showCalendar, setShowCalendar] = useState(false);

    const fetchAvailableDates = () => {
        if (clientId && clientId !== 'All') {
            GatewayService.getContractNoteDates(clientId)
                .then(setAvailableDates)
                .catch(err => {
                    console.error("Failed to fetch available contract note dates:", err);
                    toast.error("Could not load contract note dates.");
                });
        } else {
            setAvailableDates([]);
        }
    };

    const handleDateChange = (newDate: Date | null | [Date | null, Date | null]) => {
        if (newDate instanceof Date) {
            setDate(TimeUtil.toUIDate(newDate));
            setShowCalendar(false);
        }
    };

    const tileContent = ({ date: d, view }: { date: Date, view: string }) => {
        if (view === 'month') {
            const dateStr = TimeUtil.toUIDate(d);
            if (availableDates.includes(dateStr)) {
                return <div style={{height: '5px', width: '5px', backgroundColor: 'green', borderRadius: '50%', margin: 'auto', marginTop: '3px'}}></div>;
            }
        }
        return null;
    };

    const fetchNote = async () => {
        if (!clientId) return;
        const apiDate = date;
        if (!/^\d{2}-\d{2}-\d{4}$/.test(apiDate)) {
            toast.error("Invalid date format. Please use DD-MM-YYYY");
            return;
        }
        setLoading(true);
        try {
            let finalNote: ContractNote | null = null;
            if (clientId === 'All') {
                const allNotes = await Promise.all(clients.map(c => GatewayService.getContractNoteByDate(c, apiDate, isPreview)));
                const mergedNote: ContractNote = {
                    clientId: 'All',
                    date: apiDate,
                    broughtForward: [],
                    optionTrades: [],
                    futureTrades: [],
                    exchangeTrades: [],
                };
                for (const note of allNotes) {
                    if (note) {
                        mergedNote.broughtForward.push(...(note.broughtForward || []));
                        mergedNote.optionTrades.push(...(note.optionTrades || []));
                        mergedNote.futureTrades.push(...(note.futureTrades || []));
                        mergedNote.exchangeTrades.push(...(note.exchangeTrades || []));
                    }
                }
                finalNote = mergedNote;
            } else {
                finalNote = await GatewayService.getContractNoteByDate(clientId, apiDate, isPreview);
            }
            setNote(finalNote);
        } catch (err) {
            console.error(err);
            setNote(null);
            toast.error("Failed to fetch contract note.");
        } finally {
            setLoading(false);
        }
    };

    const generateNote = () => {
        if (!clientId) return;
        const apiDate = date;
        if (!/^\d{2}-\d{2}-\d{4}$/.test(apiDate)) {
            toast.error("Invalid date format. Please use DD-MM-YYYY");
            return;
        }
        toast("Generating note...");
        setLoading(true);
        GatewayService.generateContractNote(clientId, apiDate)
            .then(resp => {
                toast.success("Generated: " + JSON.stringify(resp));
                fetchNote();
                fetchAvailableDates();
            })
            .catch(err => {
                toast.error("Failed to generate contract note: " + err.message);
                setLoading(false);
            });
    }

    const persistPositions = () => {
        toast("Persisting positions...");
        setLoading(true);
        GatewayService.persistPositions()
            .then(resp => toast.success("Persisted: " + JSON.stringify(resp)))
            .catch(err => toast.error("Failed to persist positions: " + err.message))
            .finally(()=>setLoading(false));
    }

    const persistClosings = () => {
        const apiDate = date;
        if (!/^\d{2}-\d{2}-\d{4}$/.test(apiDate)) {
            toast.error("Invalid date format. Please use DD-MM-YYYY");
            return;
        }
        toast("Persisting closings...");
        setLoading(true);
        GatewayService.persistClosings(apiDate)
            .then(resp => toast.success("Persisted closings: " + JSON.stringify(resp)))
            .catch(err => toast.error("Failed to persist closings: " + err.message))
            .finally(()=>setLoading(false));
    }

    const changeDay = (offset: number) => {
        const currentDate = TimeUtil.parseUIDate(date);
        if (currentDate) {
            currentDate.setDate(currentDate.getDate() + offset);
            setDate(TimeUtil.toUIDate(currentDate));
        }
    };

    const setYesterday = () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = TimeUtil.toUIDate(yesterday);
        setDate(yesterdayStr);
    }

    useEffect(()=>{
        if (!clientId && clients && clients.length>0) setClientId(clients[0]);
    }, [clients]);

    useEffect(() => {
        fetchAvailableDates();
    }, [clientId]);

    useEffect(()=>{
        if (!clientId) return;
        fetchNote();
    }, [clientId, date]);

    const summary = useMemo(()=>{
        const all: Note[] = note ? (
            (note.broughtForward||[])
                .concat(note.optionTrades||[])
                .concat(note.futureTrades||[])
                .concat(note.exchangeTrades||[])
        ) : [];
        const payInOut = all.reduce((a,b)=>a+computePayOut(b), 0);
        const brokerage = all.reduce((a,b)=>a+(b.brokerage||0), 0);
        const taxes = all.reduce((a,b)=>a+(b.taxes||0), 0);
        const net = payInOut - brokerage - taxes;
        return {payInOut, brokerage, taxes, net};
    }, [note]);

    return <div className="container">
        <h2>Contract Notes</h2>
        <div className="positions-header">
            <div className="positions-controls">
                <div className="control-group">
                    <h3>Client</h3>
                    <DropDownCompString text={"Client"} actual={clientId} arr={getKeyVal(['All', ...(clients||[])])} set={setClientId}/>
                </div>
                <div className="control-group">
                    <h3>Date</h3>
                    <input type="text" value={date} onChange={e => setDate(e.target.value)} placeholder="DD-MM-YYYY"/>
                    <div style={{display: 'inline-flex'}}>
                        <button onClick={() => changeDay(-1)}>➖</button>
                        <button onClick={setYesterday}>Yesterday</button>
                        <button onClick={() => changeDay(1)}>➕</button>
                    </div>
                    <button onClick={() => setShowCalendar(s => !s)}>{showCalendar ? 'Hide' : 'Show'} Calendar</button>
                    {showCalendar && <Calendar
                        onChange={handleDateChange}
                        value={TimeUtil.parseUIDate(date) || new Date()}
                        tileContent={tileContent}
                    />}
                </div>
                <div className="control-group">
                    <h3>Actions</h3>
                    <label style={{ marginRight: '10px' }}>
                        <input
                            type="checkbox"
                            checked={isPreview}
                            onChange={() => setIsPreview(!isPreview)}
                        />
                        Preview
                    </label>
                    <button onClick={fetchNote} disabled={loading}>{loading ? "Refreshing..." : (isPreview ? "Preview" : "Fetch")}</button>
                    <button onClick={generateNote} disabled={loading || clientId === 'All'}>Generate</button>
                    <button onClick={persistPositions} disabled={loading}>Persist Today's Positions</button>
                    <button onClick={persistClosings} disabled={loading}>Persist Closings</button>
                </div>
                <div className="control-group">
                    <h3>Summary (Top)</h3>
                    <div className="positions-summary-row">
                        <div className="positions-summary-card">Pay in/Pay out obligation: <Money val={summary.payInOut}/></div>
                        <div className="positions-summary-card">Brokerage: <Money val={summary.brokerage}/></div>
                        <div className="positions-summary-card">Taxes: <Money val={summary.taxes}/></div>
                        <div className="positions-summary-card">Net amount receivable/(payable): <Money val={summary.net}/></div>
                    </div>
                </div>
            </div>
            <div className="expiry-filters">
                <h3>Notes</h3>
                {loading && <div>Loading...</div>}
                {!loading && !note && <div>No contract note.</div>}
                {!loading && note && (
                    <div className="positions-list" style={{marginTop: 8}}>
                        <div className="positions-list-row">
                            <div className="positions-list-cell"><strong>Date:</strong> {note.date}</div>
                            <div className="positions-list-cell"><strong>Client:</strong> {note.clientId}</div>
                        </div>
                        <Section title="Brought Forward" rows={note.broughtForward||[]}/>
                        <Section title="Option Trades" rows={note.optionTrades||[]}/>
                        <Section title="Future Trades" rows={note.futureTrades||[]}/>
                        <Section title="Exchange Trades" rows={note.exchangeTrades||[]}/>
                    </div>
                )}
            </div>
            <div className="control-group" style={{marginTop: 8}}>
                <h3>Summary (Bottom)</h3>
                <div className="positions-summary-row">
                    <div className="positions-summary-card">Pay in/Pay out obligation: <Money val={summary.payInOut}/></div>
                    <div className="positions-summary-card">Brokerage: <Money val={summary.brokerage}/></div>
                    <div className="positions-summary-card">Taxes: <Money val={summary.taxes}/></div>
                    <div className="positions-summary-card">Net amount receivable/(payable): <Money val={summary.net}/></div>
                </div>
            </div>
        </div>
    </div>
}

export default ContractNoteComponent;


